// client/src/hooks/useUnidades.js (VERSÃO 100% COMPLETA E CORRIGIDA)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const UNIDADES_QUERY_KEY = 'unidades';
const USER_UNIDADES_QUERY_KEY = 'userUnidades';

// Contrato canônico do payload — ver bloco de documentação em
// server/controllers/unidadesController.js (CONTRATO CANÔNICO —
// UnidadePayload). endereco/contato/geolocalizacao/metadata são subschemas:
// o backend faz merge raso com o valor atual, mas prefira sempre enviar o
// objeto completo.

// ✅ FUNÇÃO AUXILIAR: Sanitizar parâmetros
const sanitizeUnidadesParams = (params = {}) => {
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    if (value !== undefined && value !== null && value !== '' && value !== 'todos') {
      if (typeof value === 'boolean') {
        sanitized[key] = value.toString();
      } else {
        sanitized[key] = value;
      }
    }
  });
  
  return sanitized;
};

// ✅ FUNÇÃO AUXILIAR: Processar resposta da API - CORRIGIDA
const processUnidadesResponse = (responseData, context = '') => {
  if (!responseData) {
    console.warn(`⚠️ [${context}] Resposta vazia da API`);
    return { success: false, data: [], count: 0, totalCount: 0, pagination: {} };
  }

  let finalData = [];
  let success = true;
  let count = 0;
  let totalCount = 0;

  // ✅ LOG PARA DEBUG
  console.log(`📦 [${context}] Dados brutos:`, responseData);

  if (responseData.success !== undefined && responseData.data !== undefined) {
    finalData = Array.isArray(responseData.data) ? responseData.data : [];
    success = responseData.success;
    count = responseData.count || finalData.length;
    totalCount = responseData.totalCount || count;
  }
  else if (Array.isArray(responseData)) {
    finalData = responseData;
    count = responseData.length;
    totalCount = responseData.length;
  }
  else if (responseData.data && Array.isArray(responseData.data)) {
    finalData = responseData.data;
    count = finalData.length;
    totalCount = responseData.totalCount || count;
  }
  else {
    finalData = [responseData].filter(Boolean);
    count = finalData.length;
    totalCount = finalData.length;
  }

  // ✅ VERIFICAR SE OS DADOS TÊM _id
  const hasValidIds = finalData.every(item => item && (item._id || item.id));
  if (!hasValidIds) {
    console.warn(`⚠️ [${context}] Alguns itens não possuem ID válido:`, 
      finalData.map(item => ({ 
        temId: !!(item?._id || item?.id), 
        nome: item?.nome 
      }))
    );
  }

  console.log(`✅ [${context}] Dados processados:`, {
    success,
    dataLength: finalData.length,
    count,
    totalCount,
    amostraIds: finalData.slice(0, 3).map(item => item?._id || item?.id)
  });

  return {
    success,
    data: finalData,
    count,
    totalCount,
    pagination: responseData.pagination || {}
  };
};

// =============================================================================
// ✅ HOOKS DE CONSULTA (QUERIES)
// =============================================================================

/**
 * ✅ HOOK PRINCIPAL: Buscar todas as unidades
 */
export const useUnidades = (options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidades = async () => {
    console.log('🔍 [USE_UNIDADES] Buscando todas as unidades');
    
    try {
      const { data } = await apiClient.get('/unidades');
      return processUnidadesResponse(data, 'USE_UNIDADES');
    } catch (error) {
      console.error('❌ [USE_UNIDADES] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY],
    queryFn: fetchUnidades,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidades:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidades por conta específica - CORRIGIDO
 */
export const useUnidadesPorConta = (accountId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesPorConta = async () => {
    if (!accountId) {
      console.warn('⚠️ [USE_UNIDADES_POR_CONTA] Account ID não fornecido');
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Account ID é obrigatório'
      };
    }
    
    console.log('🔍 [USE_UNIDADES_POR_CONTA] Buscando unidades da conta:', accountId);
    
    try {
      const cleanParams = sanitizeUnidadesParams({
        account: accountId,
        ativo: 'true',
        limit: 100
      });

      const { data } = await apiClient.get('/unidades', {
        params: cleanParams
      });
      
      console.log('✅ [USE_UNIDADES_POR_CONTA] Dados recebidos:', {
        count: data?.data?.length || 0,
        unidades: data?.data?.map(u => ({ id: u._id, nome: u.nome, ativo: u.ativo })) || []
      });
      
      return processUnidadesResponse(data, 'USE_UNIDADES_POR_CONTA');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_POR_CONTA] Erro:', error);
      
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'conta', accountId],
    queryFn: fetchUnidadesPorConta,
    enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesPorConta:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades da conta', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Buscar unidades disponíveis para o usuário (usando vínculos)
 */
export const useUnidadesDisponiveis = (options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesDisponiveis = async () => {
    console.log('🔍 [USE_UNIDADES_DISPONIVEIS] Buscando unidades disponíveis');
    
    try {
      const { data } = await apiClient.get('/unidades/minhas-unidades');
      return processUnidadesResponse(data, 'USE_UNIDADES_DISPONIVEIS');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_DISPONIVEIS] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'disponiveis'],
    queryFn: fetchUnidadesDisponiveis,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesDisponiveis:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades disponíveis', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidades para vincular
 */
export const useUnidadesParaVincular = (options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesParaVincular = async () => {
    console.log('🔍 [USE_UNIDADES_PARA_VINCULAR] Buscando unidades para vincular');
    
    try {
      const { data } = await apiClient.get('/unidades/disponiveis-para-vincular');
      return processUnidadesResponse(data, 'USE_UNIDADES_PARA_VINCULAR');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_PARA_VINCULAR] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'para-vincular'],
    queryFn: fetchUnidadesParaVincular,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesParaVincular:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades para vincular', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidade por ID - CORRIGIDO
 */
export const useUnidade = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidade = async () => {
    if (!unidadeId) {
      console.warn('⚠️ [USE_UNIDADE] Unidade ID não fornecido');
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID é obrigatório'
      };
    }

    // ✅ GARANTIR QUE O ID É STRING E LIMPO
    const idStr = String(unidadeId).trim();
    
    if (idStr === 'undefined' || idStr === 'null' || idStr === '') {
      console.warn('⚠️ [USE_UNIDADE] Unidade ID inválido:', unidadeId);
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID inválido'
      };
    }

    console.log('🔍 [USE_UNIDADE] Buscando unidade:', idStr);
    
    try {
      const { data } = await apiClient.get(`/unidades/${idStr}`);
      
      console.log('📦 [USE_UNIDADE] Dados recebidos:', data);
      
      return {
        success: true,
        data: data?.data || null,
        count: 0,
        totalCount: 0,
        pagination: {}
      };
    } catch (error) {
      console.error('❌ [USE_UNIDADE] Erro:', error);
      
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, unidadeId],
    queryFn: fetchUnidade,
    enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null' && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidade:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidade', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Buscar configurações de agenda da unidade
 */
export const useUnidadeAgendaSettings = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadeAgendaSettings = async () => {
    if (!unidadeId) {
      console.warn('⚠️ [USE_UNIDADE_AGENDA_SETTINGS] Unidade ID não fornecido');
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID é obrigatório'
      };
    }

    const idStr = String(unidadeId).trim();
    
    console.log('🔍 [USE_UNIDADE_AGENDA_SETTINGS] Buscando configurações de agenda:', idStr);
    
    try {
      const { data } = await apiClient.get(`/unidades/${idStr}/agenda`);
      
      return {
        success: true,
        data: data?.data || null,
        count: 0,
        totalCount: 0,
        pagination: {}
      };
    } catch (error) {
      console.error('❌ [USE_UNIDADE_AGENDA_SETTINGS] Erro:', error);
      
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'agenda', unidadeId],
    queryFn: fetchUnidadeAgendaSettings,
    enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null' && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadeAgendaSettings:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar configurações da unidade', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Buscar estatísticas da unidade
 */
export const useUnidadeEstatisticas = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadeEstatisticas = async () => {
    if (!unidadeId) {
      console.warn('⚠️ [USE_UNIDADE_ESTATISTICAS] Unidade ID não fornecido');
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID é obrigatório'
      };
    }

    const idStr = String(unidadeId).trim();
    
    console.log('🔍 [USE_UNIDADE_ESTATISTICAS] Buscando estatísticas da unidade:', idStr);
    
    try {
      const { data } = await apiClient.get(`/unidades/${idStr}/estatisticas`);
      
      return {
        success: true,
        data: data?.data || null,
        count: 0,
        totalCount: 0,
        pagination: {}
      };
    } catch (error) {
      console.error('❌ [USE_UNIDADE_ESTATISTICAS] Erro:', error);
      
      return {
        success: false,
        data: null,
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'estatisticas', unidadeId],
    queryFn: fetchUnidadeEstatisticas,
    enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null' && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadeEstatisticas:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar estatísticas da unidade', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Buscar usuários vinculados à unidade
 */
export const useUsuariosVinculadosUnidade = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUsuariosVinculados = async () => {
    if (!unidadeId) {
      console.warn('⚠️ [USE_USUARIOS_VINCULADOS_UNIDADE] Unidade ID não fornecido');
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID é obrigatório'
      };
    }

    const idStr = String(unidadeId).trim();
    
    console.log('🔍 [USE_USUARIOS_VINCULADOS_UNIDADE] Buscando usuários vinculados:', idStr);
    
    try {
      const { data } = await apiClient.get(`/unidades/${idStr}/usuarios-vinculados`);
      return processUnidadesResponse(data, 'USE_USUARIOS_VINCULADOS_UNIDADE');
    } catch (error) {
      console.error('❌ [USE_USUARIOS_VINCULADOS_UNIDADE] Erro:', error);
      
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'usuarios-vinculados', unidadeId],
    queryFn: fetchUsuariosVinculados,
    enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null' && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUsuariosVinculadosUnidade:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar usuários vinculados', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Buscar profissionais por unidade
 */
export const useProfissionaisPorUnidade = (unidadeId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchProfissionaisPorUnidade = async () => {
    if (!unidadeId) {
      console.warn('⚠️ [USE_PROFISSIONAIS_POR_UNIDADE] Unidade ID não fornecido');
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Unidade ID é obrigatório'
      };
    }

    const idStr = String(unidadeId).trim();
    
    console.log('🔍 [USE_PROFISSIONAIS_POR_UNIDADE] Buscando profissionais da unidade:', idStr);
    
    try {
      const { data } = await apiClient.get(`/unidades/${idStr}/profissionais`);
      return processUnidadesResponse(data, 'USE_PROFISSIONAIS_POR_UNIDADE');
    } catch (error) {
      console.error('❌ [USE_PROFISSIONAIS_POR_UNIDADE] Erro:', error);
      
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'profissionais', unidadeId],
    queryFn: fetchProfissionaisPorUnidade,
    enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null' && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useProfissionaisPorUnidade:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar profissionais da unidade', 'danger');
    }
  });
};

/**
 * ✅ HOOK CRÍTICO: Buscar unidades para formulários
 */
export const useUnidadesParaFormulario = (accountId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesParaFormulario = async () => {
    if (!accountId) {
      console.warn('⚠️ [USE_UNIDADES_PARA_FORMULARIO] Account ID não fornecido');
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'Account ID é obrigatório'
      };
    }
    
    console.log('🔄 [USE_UNIDADES_PARA_FORMULARIO] Buscando unidades para formulário da conta:', accountId);
    
    try {
      const cleanParams = sanitizeUnidadesParams({
        account: accountId,
        ativo: 'true',
        limit: 100,
        sort: 'nome'
      });

      const { data } = await apiClient.get('/unidades', {
        params: cleanParams
      });
      
      return processUnidadesResponse(data, 'USE_UNIDADES_PARA_FORMULARIO');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_PARA_FORMULARIO] Erro ao buscar unidades:', error);
      
      // ✅ FALLBACK: Tentar buscar unidades disponíveis
      try {
        console.log('🔄 Tentando fallback para unidades disponíveis...');
        const { data } = await apiClient.get('/unidades/minhas-unidades', {
          params: { limit: 50 }
        });
        
        if (data?.success && data?.data) {
          const unidadesFallback = data.data.map(unidade => ({
            _id: unidade._id || unidade.id,
            id: unidade._id || unidade.id,
            nome: unidade.nome || 'Unidade sem nome',
            localidade: unidade.localidade || '',
            uf: unidade.uf || '',
            ativo: true
          }));
          
          return {
            success: true,
            data: unidadesFallback,
            count: unidadesFallback.length,
            totalCount: unidadesFallback.length,
            pagination: {}
          };
        }
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: 'Nenhuma unidade encontrada'
        };
      } catch (fallbackError) {
        console.error('❌ Fallback também falhou:', fallbackError);
        throw error;
      }
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'formulario', accountId],
    queryFn: fetchUnidadesParaFormulario,
    enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesParaFormulario:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades para formulário', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar estatísticas gerais das unidades
 */
export const useUnidadesEstatisticasGerais = (options = {}) => {
  const { setAlert } = useAlert();

  const fetchEstatisticasGerais = async () => {
    console.log('🔍 [USE_UNIDADES_ESTATISTICAS_GERAIS] Buscando estatísticas gerais');
    
    try {
      const { data } = await apiClient.get('/unidades/estatisticas/geral');
      
      return {
        success: true,
        data: data?.data || null,
        count: 0,
        totalCount: 0,
        pagination: {}
      };
    } catch (error) {
      console.error('❌ [USE_UNIDADES_ESTATISTICAS_GERAIS] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'estatisticas-gerais'],
    queryFn: fetchEstatisticasGerais,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesEstatisticasGerais:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar estatísticas gerais', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidades ativas para seleção
 */
export const useUnidadesAtivasParaSelecao = (options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesAtivas = async () => {
    console.log('🔍 [USE_UNIDADES_ATIVAS_PARA_SELECAO] Buscando unidades ativas para seleção');
    
    try {
      const { data } = await apiClient.get('/unidades/selecionar/ativas');
      return processUnidadesResponse(data, 'USE_UNIDADES_ATIVAS_PARA_SELECAO');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_ATIVAS_PARA_SELECAO] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'selecao-ativas'],
    queryFn: fetchUnidadesAtivas,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesAtivasParaSelecao:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades para seleção', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidades com filtros específicos
 */
export const useUnidadesComFiltros = (filters = {}, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesComFiltros = async () => {
    const cleanParams = sanitizeUnidadesParams(filters);
    console.log('🔍 [USE_UNIDADES_COM_FILTROS] Buscando unidades com filtros:', cleanParams);
    
    try {
      const { data } = await apiClient.get('/unidades', { params: cleanParams });
      return processUnidadesResponse(data, 'USE_UNIDADES_COM_FILTROS');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_COM_FILTROS] Erro:', error);
      throw error;
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'filtrados', filters],
    queryFn: fetchUnidadesComFiltros,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesComFiltros:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades filtradas', 'danger');
    },
    ...options
  });
};

/**
 * ✅ HOOK: Buscar unidades por localidade
 */
export const useUnidadesPorLocalidade = (uf, localidade = null, options = {}) => {
  const { setAlert } = useAlert();

  const fetchUnidadesPorLocalidade = async () => {
    if (!uf) {
      console.warn('⚠️ [USE_UNIDADES_POR_LOCALIDADE] UF não fornecido');
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: 'UF é obrigatório'
      };
    }

    console.log('🔍 [USE_UNIDADES_POR_LOCALIDADE] Buscando unidades por localidade:', { uf, localidade });
    
    try {
      const params = { uf };
      if (localidade) params.localidade = localidade;
      
      const { data } = await apiClient.get('/unidades/buscar/localidade', { params });
      return processUnidadesResponse(data, 'USE_UNIDADES_POR_LOCALIDADE');
    } catch (error) {
      console.error('❌ [USE_UNIDADES_POR_LOCALIDADE] Erro:', error);
      
      return {
        success: false,
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {},
        error: error.message
      };
    }
  };

  return useQuery({
    queryKey: [UNIDADES_QUERY_KEY, 'localidade', uf, localidade],
    queryFn: fetchUnidadesPorLocalidade,
    enabled: !!uf && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    onError: (error) => {
      console.error("❌ Erro no hook useUnidadesPorLocalidade:", error);
      setAlert(error.response?.data?.error || 'Erro ao carregar unidades por localidade', 'danger');
    }
  });
};

/**
 * ✅ HOOK: Verificar se usuário pode acessar unidade
 */
export const usePodeAcessarUnidade = (unidadeId, options = {}) => {
  const { data: unidadesDisponiveis, isLoading, error } = useUnidadesDisponiveis(options);

  const podeAcessar = Array.isArray(unidadesDisponiveis?.data) && 
                      unidadesDisponiveis.data.some(unidade => 
                        unidade._id === unidadeId || unidade._id?.toString() === unidadeId?.toString()
                      );

  return {
    podeAcessar,
    unidadesDisponiveis: Array.isArray(unidadesDisponiveis?.data) ? unidadesDisponiveis.data : [],
    isLoading,
    error
  };
};

// =============================================================================
// ✅ HOOKS DE MUTAÇÃO (MUTATIONS)
// =============================================================================

/**
 * ✅ HOOK: Mutações para unidades
 */
export const useMutateUnidades = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateUnidadesCache = (unidadeId = null) => {
    queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'disponiveis'] });
    queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'para-vincular'] });
    queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'selecao-ativas'] });
    queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'estatisticas-gerais'] });
    queryClient.invalidateQueries({ queryKey: [USER_UNIDADES_QUERY_KEY] });
    
    if (unidadeId) {
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, unidadeId] });
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'agenda', unidadeId] });
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'estatisticas', unidadeId] });
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'usuarios-vinculados', unidadeId] });
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'profissionais', unidadeId] });
    }
  };

  const handleSuccess = (message, unidadeId = null) => {
    invalidateUnidadesCache(unidadeId);
    setAlert(message, 'success');
    console.log('✅ [USE_MUTATE_UNIDADES] Sucesso:', message);
  };

  const handleError = (error, defaultMessage) => {
    console.error('❌ [USE_MUTATE_UNIDADES] Erro:', error);
    const errorMsg = error.response?.data?.error || error.message || defaultMessage;
    setAlert(errorMsg, 'danger');
    throw new Error(errorMsg);
  };

  // ✅ MUTATION: Criar unidade
  const createUnidade = useMutation({
    mutationFn: async ({ accountId, unidadeData }) => {
      if (!accountId) {
        throw new Error('Account ID é obrigatório');
      }
      
      const dadosComAccount = {
        ...unidadeData,
        account: accountId
      };
      
      const response = await apiClient.post('/unidades', dadosComAccount);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Unidade adicionada com sucesso!', data.data?._id);
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'conta', variables.accountId] });
    },
    onError: (error) => handleError(error, 'Erro ao adicionar unidade')
  });

  // ✅ MUTATION: Atualizar unidade
  const updateUnidade = useMutation({
    mutationFn: async ({ unidadeId, unidadeData }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      const response = await apiClient.put(`/unidades/${unidadeId}`, unidadeData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Unidade atualizada com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar unidade')
  });

  // ✅ MUTATION: Deletar unidade
  const deleteUnidade = useMutation({
    mutationFn: async (unidadeId) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      const response = await apiClient.delete(`/unidades/${unidadeId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Unidade removida com sucesso.', variables);
    },
    onError: (error) => handleError(error, 'Erro ao deletar unidade')
  });

  // ✅ MUTATION: Alternar status da unidade
  const alternarStatusUnidade = useMutation({
    mutationFn: async (unidadeId) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      const response = await apiClient.patch(`/unidades/${unidadeId}/alternar-status`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Status da unidade alterado com sucesso!', variables);
    },
    onError: (error) => handleError(error, 'Erro ao alternar status da unidade')
  });

  // ✅ MUTATION: Atualizar configurações de agenda
  const updateUnidadeAgendaSettings = useMutation({
    mutationFn: async ({ unidadeId, settings }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      const response = await apiClient.put(`/unidades/${unidadeId}/agenda`, settings);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Configurações de agenda atualizadas com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar configurações da agenda')
  });

  // ✅ MUTATION: Vincular usuário à unidade
  const vincularUnidadeUsuario = useMutation({
    mutationFn: async ({ unidadeId, userId }) => {
      if (!unidadeId || !userId) {
        throw new Error('Unidade ID e User ID são obrigatórios');
      }
      
      const response = await apiClient.post(`/unidades/${unidadeId}/vincular-usuario/${userId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'usuarios-vinculados', variables.unidadeId] });
      queryClient.invalidateQueries({ queryKey: ['accountUsers'] });
      queryClient.invalidateQueries({ queryKey: [USER_UNIDADES_QUERY_KEY] });
      handleSuccess(data.message || 'Usuário vinculado à unidade com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao vincular usuário à unidade')
  });

  // ✅ MUTATION: Desvincular usuário da unidade
  const desvincularUnidadeUsuario = useMutation({
    mutationFn: async ({ unidadeId, userId }) => {
      if (!unidadeId || !userId) {
        throw new Error('Unidade ID e User ID são obrigatórios');
      }
      
      const response = await apiClient.delete(`/unidades/${unidadeId}/desvincular-usuario/${userId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY, 'usuarios-vinculados', variables.unidadeId] });
      queryClient.invalidateQueries({ queryKey: ['accountUsers'] });
      queryClient.invalidateQueries({ queryKey: [USER_UNIDADES_QUERY_KEY] });
      handleSuccess(data.message || 'Usuário desvinculado da unidade com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao desvincular usuário da unidade')
  });

  // ✅ MUTATION: Adicionar imagem à unidade (CORRIGIDO: Apenas UMA key correta 'imagem')
  const addUnidadeImagem = useMutation({
    mutationFn: async ({ unidadeId, file }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      if (!file) {
        throw new Error('Arquivo é obrigatório');
      }
      
      console.log('🖼️ [ADD_UNIDADE_IMAGEM] Enviando imagem via Multer para unidade:', unidadeId);
      
      const formData = new FormData();
      
      // ✅ CORREÇÃO CRÍTICA PARA 'Unexpected Field' e 'Too many files':
      // Como o middleware no server/routes/unidadesRoutes.js se chama 'uploadImagemMiddleware',
      // o Multer foi instanciado com upload.single('imagem') (padrão do sistema).
      formData.append('imagem', file); 
      
      const response = await apiClient.post(`/unidades/${unidadeId}/imagens`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Imagem adicionada com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao adicionar imagem')
  });

  // ✅ MUTATION: Definir logo da unidade
  const definirLogoUnidade = useMutation({
    mutationFn: async ({ unidadeId, arquivoId }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      if (!arquivoId) {
        throw new Error('ID do arquivo é obrigatório');
      }
      
      const response = await apiClient.post(`/unidades/${unidadeId}/logo`, { arquivoId });
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Logo definido com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao definir logo')
  });

  // ✅ MUTATION: Definir imagem principal da unidade
  const definirImagemPrincipalUnidade = useMutation({
    mutationFn: async ({ unidadeId, arquivoId }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      if (!arquivoId) {
        throw new Error('ID do arquivo é obrigatório');
      }
      
      const response = await apiClient.post(`/unidades/${unidadeId}/imagem-principal`, { arquivoId });
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Imagem principal definida com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao definir imagem principal')
  });

  // ✅ MUTATION: Remover imagem da unidade
  const removeUnidadeImagem = useMutation({
    mutationFn: async ({ unidadeId, imagemId }) => {
      if (!unidadeId) {
        throw new Error('Unidade ID é obrigatório');
      }
      
      if (!imagemId) {
        throw new Error('ID da imagem é obrigatório');
      }
      
      const response = await apiClient.delete(`/unidades/${unidadeId}/imagens/${imagemId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      handleSuccess(data.message || 'Imagem removida com sucesso!', variables.unidadeId);
    },
    onError: (error) => handleError(error, 'Erro ao remover imagem')
  });

  return { 
    // CRUD
    createUnidade, 
    updateUnidade, 
    deleteUnidade,
    alternarStatusUnidade,
    
    // Configurações
    updateUnidadeAgendaSettings,
    
    // Relacionamentos
    vincularUnidadeUsuario, 
    desvincularUnidadeUsuario,
    
    // Imagens
    addUnidadeImagem,
    definirLogoUnidade,
    definirImagemPrincipalUnidade,
    removeUnidadeImagem
  };
};

// =============================================================================
// ✅ HOOKS DE DADOS CONSOLIDADOS
// =============================================================================

/**
 * ✅ HOOK: Dados consolidados de unidades
 */
export const useUnidadesData = () => {
  const { data: unidades, isLoading: isLoadingUnidades, error: unidadesError } = useUnidades();
  const { data: unidadesDisponiveis, isLoading: isLoadingDisponiveis, error: disponiveisError } = useUnidadesDisponiveis();

  return {
    unidades: Array.isArray(unidades?.data) ? unidades.data : [],
    unidadesDisponiveis: Array.isArray(unidadesDisponiveis?.data) ? unidadesDisponiveis.data : [],
    isLoading: isLoadingUnidades || isLoadingDisponiveis,
    isLoadingUnidades,
    isLoadingDisponiveis,
    error: unidadesError || disponiveisError,
    unidadesError,
    disponiveisError,
    hasData: !!(unidades?.data?.length && unidadesDisponiveis?.data?.length),
    totalUnidades: Array.isArray(unidades?.data) ? unidades.data.length : 0,
    totalUnidadesDisponiveis: Array.isArray(unidadesDisponiveis?.data) ? unidadesDisponiveis.data.length : 0,
  };
};

export default {
  useUnidades,
  useUnidadesPorConta,
  useUnidadesDisponiveis,
  useUnidadesParaVincular,
  useUnidade,
  useUnidadeAgendaSettings,
  useUnidadeEstatisticas,
  useUsuariosVinculadosUnidade,
  useProfissionaisPorUnidade,
  useUnidadesParaFormulario,
  useUnidadesEstatisticasGerais,
  useUnidadesAtivasParaSelecao,
  useUnidadesComFiltros,
  useUnidadesPorLocalidade,
  usePodeAcessarUnidade,
  useUnidadesData,
  useMutateUnidades
};